/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { useOnboardingDataContext } from '../../context/OnboardingData';
import useGeolocation from '../../hooks/useGeolocation';
import getNextQuestionId from '../../utils/getNextQuestionId';
import FilterByLocation from '../FilterByLocation';
import LoaderWhite from '../LoaderWhite';
import useStyles from './styles';

const GeolocationQuestion = React.forwardRef(
  (
    {
      readableId,
      answers,
      onAnswer,
      selectedAge,
      headerTitle,
      nextClickAnswer,
      questionOrder,
    },
    locationRef
  ) => {
    // *** NEW STATE VARIABLE TO TRACK FOCUS ***
    const [locationHasError, setLocationHasError] = useState(false);

    // *** PASS FOCUSED ID TO STYLES HOOK ***
    const styles = useStyles();

    const { geolocationData } = useGeolocation();
    const [isSelected, setIsSelected] = useState(false);
    const [answerSelected, setAnswerSelected] = useState();
    const [statusCurrent, setStatusCurrent] = useState(false);
    const { coordinates, status } = geolocationData || {};
    const locationAnswer = answers[0];
    const postcodeAnswer = answers[1];
    const {
      OnboardingContext,
      setOnboardingDataContext,
      arrowContext,
      setArrowFunction,
    } = useOnboardingDataContext();

    const selectLocationAnswer = () => {
       if (
          OnboardingContext?.location &&
          (OnboardingContext.location.Latitude ||
            OnboardingContext.location.ReadableLocation)
        ) {
          const loc = OnboardingContext.location;
          onAnswer({
            answerData: {
              [readableId]: loc.OptforCurrentLocation === 'Yes' ? 'Yes' : 'No',
              Latitude: loc.Latitude,
              Longitude: loc.Longitude,
              ReadableLocation: loc.ReadableLocation,
            },
            nextQuestionId: null,
          });
          return;
        }
      setTimeout(() => {
        answerSelected === postcodeAnswer.title
          ? onAnswer({
              answerData: {
                [readableId]: 'No',
              },
              nextQuestionId: getNextQuestionId({
                ageRanges: postcodeAnswer.ageRanges,
                selectedAge,
              }),
            })
          : onAnswer({
              answerData: {
                [readableId]: 'Yes',
                Latitude: coordinates.latitude,
                Longitude: coordinates.longitude,
                ReadableLocation: 'Your location',
                OptforCurrentLocation: 'Yes',
              },
              nextQuestionId: getNextQuestionId({
                ageRanges: locationAnswer.ageRanges,
                selectedAge,
              }),
            });
      }, 500);
    };
    // for passing this function to question component
    useImperativeHandle(locationRef, () => ({
      selectLocationAnswer,
    }));
    useEffect(() => {
      // using 'prev' to get the current state safely.
      setOnboardingDataContext(prev => ({
        ...prev,
        location: undefined,
        title: locationAnswer.title,
        question: headerTitle,
        step: 3,
      }));
    setIsSelected(false);
    setAnswerSelected(undefined);
    setStatusCurrent(false);
    setLocationHasError(false);
    // only run on mounting phase
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = useCallback((loc) => {
      setOnboardingDataContext(prev => {
        // If everything is identical, returning 'prev' to avoid loops
    if (
      prev?.location?.ReadableLocation === loc?.ReadableLocation &&
      prev?.location?.Latitude === loc?.Latitude &&
      prev?.location?.OptforCurrentLocation === loc?.OptforCurrentLocation
    ) {
      return prev; 
    }
        return {
          ...prev,
          location: {
            Latitude: loc?.Latitude,
            Longitude: loc?.Longitude,
            ReadableLocation: loc?.ReadableLocation,
            OptforCurrentLocation: loc?.OptforCurrentLocation,
          },
        };
      });
      setLocationHasError(false);
      setArrowFunction(true);
      setStatusCurrent(true);
    }, [setOnboardingDataContext, setArrowFunction]); 

    return (
      <Fragment>
        {answers && (
         <>
            <div css={styles.question}>
              <FilterByLocation
                css={styles.answer(isSelected)}
                onSave={handleSave}
                currentValue={
                  (status === 'success' && statusCurrent) 
                    ? 'Your location' 
                    : (OnboardingContext?.location?.ReadableLocation || '')
                }
                onErrorChange={hasError => {
                  setLocationHasError(hasError);
                  if (hasError) {
                    setArrowFunction(false);
                    setStatusCurrent(false);
                  }
                }}
                locationHasError={locationHasError}
                currentGeoLocation={status === 'success' && statusCurrent}
                setCurrentGeoLocation={val => setStatusCurrent(val)}
                onManualInput={() => {
                  setArrowFunction(false);
                }}
                preferences={{}}
                filterShowHide={false}
                magnifierIconVariant="light"
                LoaderComponent={LoaderWhite}
                isSmall={true}
              />
             </div>
            <button
              id={`question_${questionOrder}_next`}
              aria-label="Next"
              css={styles.buttonNext(arrowContext)}
              disabled={arrowContext === false}
              onClick={() => {
                nextClickAnswer();
                setArrowFunction(false);
                selectLocationAnswer();
              }}
            >
              Next
            </button>
         </>
        )}
      </Fragment>
    );
  }
);

GeolocationQuestion.prototype = {
  readableId: PropTypes.string,
  answers: PropTypes.object,
  onAnswer: PropTypes.func,
  selectedAge: PropTypes.string,
  headerTitle: PropTypes.string,
};

export default GeolocationQuestion;
